<template>
  <div>
    <!----------------------PARTS ----------------------->
    <div class="mb-5"
      v-if="selectedTab.type == ItemCategoryTypes.PART || selectedViewType != viewTypes.tabbedView">
      <quote-builder-part-headings-component :name="partCategoryObject?.displayName || 'Parts'"
        :count="partLength"
        :total-amount="partsTotal"
        :is-full-width="isFullWidth" />
      <div class="rows">
        <draggable @change="itemMoved($event, ItemCategoryTypes.PART)"
          v-model="innerValue.parts"
          @over.prevent
          @enter.prevent
          :clone="$event => cloneHandler($event, ItemCategoryTypes.PART)"
          :group="{ name: ItemCategoryTypes.PART, pull: 'clone', put: true }"
          v-bind="{'disabled': !isRowDraggable, ghostClass: 'draggable-ghost', dragClass: 'draggable-drag'}"
          handle=".item-drag-handle">
          <quote-builder-part-component-item v-for="item in innerValue.parts"
            :key="item.quoteItemId"
            :inner-value="innerValue"
            :is-full-width="isFullWidth"
            :items-assessments="itemsAssessments"
            :item="item"
            :read-only-view="readOnlyView"
            :is-selected="editItemObj.id === item.quoteItemId"
            :vehicle="vehicle"
            :labour-type="innerValue.labourType"
            :sub-quote-no="innerValue.subQuoteNo"
            :quoting-method="innerValue.quotingMethod"
            :show-assessment="(innerValue.quoteAssessments?.length || 0) > 0"
            :next-supplemetry-no="nextSupplemetryNo"
            :orm-object="ormObject"
            :next-line-number="nextLineNumber"
            :is-editable="!readOnlyComputed"
            @deleteItem="deleteItem"
            @goToPartsCheck="goToPartsCheck"
            @focusNextRow="$emit('focusNextRow', item)"
            @handlePresentAs="itemId => $emit('handlePresentAs', itemId)"
            @itemRowClicked="(value, event, close) => $emit('handleItemRowClick',value, event, close)" />
        </draggable>
      </div>
      <div>
        <button class="button is-outlined is-primary"
          @click="addBlankItem()"
          :disabled="readOnlyComputed">
          + Add Item
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import QuoteBuilderPartHeadingsComponent from './QuoteBuilderPartHeadingsComponent.vue'
import QuoteBuilderPartComponentItem from './QuoteBuilderPartItem.vue'
import draggable from 'vuedraggable'
import { QuoteItemModel } from '@/classes/viewmodels'
import { ItemCategoryTypes, PartTypes, QuoteItemRevTypes, EventHubTypes } from '@/enums'
import cloneDeep from 'lodash.clonedeep'
import QuoteRoutes from '../../../route-types'
import { PartsCheckService } from '../../../services'
import Guid from '@/components/Guid'
import { QuoteTotalsMethodMixin, QuoteAssessmentMixin, QuoteItemValidationMixin, QuotePartMixin, QuoteItemAutoSaveDeleteMixin } from '../../mixins'

export default {
  name: 'QuoteBuilderPartComponent',
  components: {
    QuoteBuilderPartHeadingsComponent,
    QuoteBuilderPartComponentItem,
    draggable
  },
  mixins: [QuoteTotalsMethodMixin, QuoteAssessmentMixin, QuoteItemValidationMixin, QuotePartMixin, QuoteItemAutoSaveDeleteMixin],
  props: {
    value: {
      type: Object,
      required: true
    },
    isFullWidth: {
      type: Boolean,
      required: true
    },
    editItemObj: {
      type: Object
    },
    selectedTab: {
      type: Object
    },
    selectedViewType: {
      type: String
    },
    isAudanet: {
      type: Boolean,
      default: false
    },
    viewTypes: {
      type: Object
    },
    vehicle: {
      type: Object
    },
    nextLineNumber: {
      type: Number,
      default: 0
    },
    isEditable: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      innerValue: null,
      isSyncingParentUpdate: false,
      lastEmittedPartsSignature: null,
      lastEmittedPaintLoadingSig: null
    }
  },
  computed: {
    ormObject() {
      return {
        orm: this.innerValue.orm,
        ormHistory: this.innerValue.parts
      }
    },
    ItemCategoryTypes() {
      return ItemCategoryTypes
    },
    partsTotal() {
      return this.calculateItemCategoryTotal(this.innerValue.quotingMethod, this.innerValue.parts, ItemCategoryTypes.PART)
    },
    isRowDraggable() {
      return !this.readOnlyComputed
    },
    itemsAssessments() {
      return this.getItemsAssessments(this.innerValue.parts, this.innerValue.quoteAssessments)
    },
    partLength() {
      // sum .itemQuantity for all parts
      return this.innerValue.parts.filter((p) => !(p.deleted || p.isDeleted))?.length || 0
    },
    readOnlyView() {
      return this.readOnlyComputed
    },
    nextSupplemetryNo() {
      if (!this.innerValue.quoteAssessments) return 1
      const nextSuppNo = Math.max(...this.innerValue.quoteAssessments.map((i) => i.subQuoteNo)) + 1
      return nextSuppNo
    },
    partCategoryObject() {
      return this.$company.itemCategories.find((i) => i.itemCategoryType === ItemCategoryTypes.PART)
    },
    readOnlyComputed() {
      return this.innerValue.readOnly || !this.isEditable
    }
  },
  watch: {
    value: {
      handler(newVal) {
        if (!newVal) return
        const currentSignature = this.computePartsSignature(newVal.parts)
        if (this.isSyncingParentUpdate && currentSignature === this.lastEmittedPartsSignature) {
          this.isSyncingParentUpdate = false
          return
        }
        // Fast-path: skip deepDiff(entire_entity) when only sibling-derived totals changed.
        // Paint-loading labours must also match: this component owns them and decides
        // splice-vs-soft-delete from their isNew flag, so a parent-side change (autosave
        // flipping isNew=false after a PUT, or the post-save server refresh) must fall
        // through to the full reset below or innerValue keeps a stale copy.
        const innerPartsSignature = this.computePartsSignature(this.innerValue?.parts)
        const currentPaintLoadingSig = this.computePartsSignature((newVal.labours || []).filter(l => l.isLoadItem))
        const innerPaintLoadingSig = this.computePartsSignature((this.innerValue?.labours || []).filter(l => l.isLoadItem))
        if (currentSignature === innerPartsSignature &&
            currentPaintLoadingSig === innerPaintLoadingSig &&
            this.innerValue?.quotingMethod === newVal.quotingMethod &&
            this.innerValue?.readOnly === newVal.readOnly) {
          return
        }
        // Full reset — parent's parts changed (e.g. recalculated totals on echo-back).
        // Before resetting, capture any paint-loading items that addUpdatePartPaintLoading
        // added to innerValue.labours but the parent hasn't yet received/processed.
        // Without this, the reset wipes them and the subsequent innerValue watcher fire
        // emits with empty paint-loading, cancelling the previous paint-loading emit.
        const pendingPaintLoading = (this.innerValue?.labours || []).filter(l => {
          if (!l.isLoadItem) return false
          const serverLabours = newVal.labours || []
          // Exact GUID match — server already has this item
          if (serverLabours.some(vl => vl.quoteItemId === l.quoteItemId)) return false
          // Server assigned a new GUID but saved a paint loading for the same linked part
          // (e.g. after a full manual save + reload). Don't re-add in that case.
          if (l.linkedItemId && serverLabours.some(vl => vl.linkedItemId === l.linkedItemId)) return false
          return true
        })
        // Preserve any in-progress edits on the currently-selected part so that a
        // sibling-triggered reset doesn't overwrite what the user is actively typing.
        const selectedId = this.editItemObj?.id
        const inProgressPart = selectedId
          ? (this.innerValue?.parts || []).find(p => p.quoteItemId === selectedId)
          : null
        this.innerValue = cloneDeep(newVal)
        pendingPaintLoading.forEach(item => this.innerValue.labours.push(cloneDeep(item)))
        if (inProgressPart) {
          const resetPart = this.innerValue.parts.find(p => p.quoteItemId === selectedId)
          if (resetPart) Object.assign(resetPart, cloneDeep(inProgressPart))
        }
        // Sync the signature to match the post-reset innerValue.labours so the
        // reset-induced innerValue watcher fire doesn't see a false paintLoadingChanged
        // and emit a stale state that wipes the pending items.
        this.lastEmittedPaintLoadingSig = this.computePartsSignature(
          this.innerValue.labours.filter(l => l.isLoadItem)
        )
      },
      deep: true
    },
    innerValue: {
      handler(newVal) {
        if (!newVal) return
        const nextSignature = this.computePartsSignature(newVal.parts)
        // Also track paint-loading labours so they propagate even when parts don't change.
        // (addUpdatePartPaintLoading is async — it may complete after the initial parts emit.)
        const nextPaintLoadingSig = this.computePartsSignature((newVal.labours || []).filter(l => l.isLoadItem))
        const partsChanged = nextSignature !== this.lastEmittedPartsSignature
        const paintLoadingChanged = nextPaintLoadingSig !== this.lastEmittedPaintLoadingSig
        if (!partsChanged && !paintLoadingChanged) return
        // Respect the sync guard for parts echoes, but not for async paint-loading completions.
        if (this.isSyncingParentUpdate && !paintLoadingChanged) return
        this.lastEmittedPartsSignature = nextSignature
        this.lastEmittedPaintLoadingSig = nextPaintLoadingSig
        this.isSyncingParentUpdate = true
        // Use value.labours as authoritative base (includes labours added by sibling components).
        // Only merge in paint-loading items from innerValue that aren't in value yet — these are
        // produced asynchronously by addUpdatePartPaintLoading and must not be lost.
        const toEmit = { ...this.value, parts: cloneDeep(newVal.parts), labours: this.computeEmitLabours(newVal.labours) }
        this.$emit('handleUpdate', toEmit)
      },
      deep: true
    }
  },
  created() {
    // compare two objects to see if they are the same, sometimes the parent
    // value might be updated by innervalue and we don't want to update the inner value again
    this.innerValue = cloneDeep(this.value)
    this.lastEmittedPartsSignature = this.computePartsSignature(this.value?.parts)
    this.lastEmittedPaintLoadingSig = this.computePartsSignature((this.value?.labours || []).filter(l => l.isLoadItem))
  },
  mounted() {
    this.$v.entity.parts.$touch()
    // event hub to update the parts
    this.$eventHub.$on('updateParts', this.updateParts)
    this.$eventHub.$on('addBlankItemPart', this.addBlankItem)
    this.$eventHub.$on('deletePart', this.deleteItem)
  },
  beforeDestroy() {
    this.$eventHub.$off('updateParts', this.updateParts)
    this.$eventHub.$off('addBlankItemPart', this.addBlankItem)
    this.$eventHub.$off('deletePart', this.deleteItem)
  },
  methods: {
    computePartsSignature(parts = []) {
      try {
        return JSON.stringify(parts)
      } catch (e) {
        return null
      }
    },
    // Build the labours array to include in a handleUpdate emit.
    //
    // Ownership split:
    //  - Regular labours are managed by sibling components (e.g. QuoteBuilderLabourComponent).
    //    Use value.labours as the authoritative source so sibling-added rows are never lost
    //    even when innerValue.labours is stale (the value-watcher fast-path skips reset when
    //    only labours changed from a sibling).
    //  - Paint-loading labours (isLoadItem) are exclusively managed by this component via
    //    addUpdatePartPaintLoading / deletePaintLoadingItem. Use innerValue.labours as the
    //    authoritative source so both async additions AND synchronous deletions are captured.
    computeEmitLabours(innerLabours) {
      const parentLabours = this.value?.labours || []
      const parentRegularLabours = parentLabours.filter(l => !l.isLoadItem)
      const innerPaintLoadingLabours = (innerLabours || []).filter(l => l.isLoadItem)
      const merged = [
        ...cloneDeep(parentRegularLabours),
        ...innerPaintLoadingLabours.map(innerItem => {
          // For items being deleted, prefer the parent's current values (which may have been
          // updated by the labour component with a non-zero hourValue/dollarValue) and apply
          // the deletion status from innerValue. This ensures labourTotal changes when the item
          // is removed, so the labourTotal watcher fires and addUpdatePaintConsumable runs to
          // clean up any associated misc items (consumables).
          // Deleted isNew items skip the parentItem lookup and pass through as-is below;
          // the autosave drops never-synced (isNew) deleted items from the PUT payload
          // and removes them from the live arrays itself.
          if ((innerItem.deleted || innerItem.isDeleted) && !innerItem.isNew) {
            const parentItem = parentLabours.find(l => l.quoteItemId === innerItem.quoteItemId)
            if (parentItem) {
              return cloneDeep({
                ...parentItem,
                deleted: innerItem.deleted,
                isDeleted: innerItem.isDeleted,
                itemStatus: innerItem.itemStatus
              })
            }
          }
          return cloneDeep(innerItem)
        })
      ]
      // Emit labours in sortNo order. The labour lists render with `v-for` over the array
      // (in array order), and the server orders items by SortNo on reload
      // (AddFromEntity -> OrderBy SortNo). Previously every linked (isLoadItem) labour was
      // concatenated LAST, so a linked item (paint loading linked to a part OR to a repair
      // labour) that the user dragged among other rows snapped back to the bottom of its
      // section. Sorting the merged result by sortNo keeps the rendered order consistent
      // with the persisted/reloaded order for all linked-item variants. Filtering stays on
      // the generic isLoadItem flag, so this is not paint-loading-specific. Idempotent:
      // itemMoved already renumbers sortNo in array order, so normal (non-linked) rows are
      // unaffected.
      return merged.sort((a, b) => (a.sortNo ?? 0) - (b.sortNo ?? 0))
    },
    updateParts() {
      const newItems = this.innerValue.parts.filter((i) => i.isNew)

      this.innerValue = cloneDeep(this.value)

      // if there are new items, add them to the innerValue
      if (newItems.length) {
        newItems.forEach((item) => {
          // check if the item already exists in the innerValue
          const existingItem = this.innerValue.parts.find((i) => i.itemDesc === item.itemDesc && i.itemNo === item.itemNo)
          if (!existingItem && item.quoteItemId == this.editItemObj.id) {
            // if it doesn't exist, add it
            this.innerValue.parts.push(item)
          }
        })
      }
      // compare two objects, sometimes this function may be called internally
      // and we don't want to update the value twice
      // const diff = deepDiff(this.innerValue, this.value)
      // if (diff) {
      //   // check if there are any changes in the parts array from parent/child components
      //   const hasPartsDiff = diff.some((d) => d.path && d.path[0] === 'parts')
      //   const newInnerValue = cloneDeep(this.value)
      //   // if parent/child Parts arrays are different, merge them
      //   if (hasPartsDiff) {
      //     // items that exist in child but not in parent (could be added when tabbing, enter or arrow down)
      //     const newPartItems = this.innerValue.parts.filter((i) => i.isNew)
      //     // if these items don't exist in the new value, add them
      //     newPartItems.forEach((item) => {
      //       if (!newInnerValue.parts.find((i) => i.itemDesc === item.itemDesc && i.itemNo === item.itemNo)) {
      //         newInnerValue.parts.push(item)
      //       }
      //     })
      //   }
      //   this.innerValue = newInnerValue
      // }
    },
    cloneHandler(item, type) {
      return item
    },
    itemMoved(event, type) {
      // if an item was dragged from one list to another, i.e from RR to RWA, Parts to RR etc.
      if (event?.added) {
        const item = event.added.element
        this.handleItemAdded(item, type, event.added.newIndex)
      }

      let sortNo = this.innerValue.parts.length ? Math.min(...this.innerValue.parts.map((i) => i.sortNo || 1)) : 1
      this.innerValue.parts.forEach((item) => {
        item.sortNo = sortNo
        sortNo++
      })
      this.$eventHub.$emit(EventHubTypes.AutoSaveQuoteItems)
      // Emit directly — don't rely on the deep watcher to detect in-place sortNo mutations
      const nextSignature = this.computePartsSignature(this.innerValue.parts)
      this.lastEmittedPartsSignature = nextSignature
      this.lastEmittedPaintLoadingSig = this.computePartsSignature((this.innerValue.labours || []).filter(l => l.isLoadItem))
      this.isSyncingParentUpdate = true
      this.$emit('handleUpdate', { ...this.value, parts: cloneDeep(this.innerValue.parts), labours: this.computeEmitLabours(this.innerValue.labours) })
      this.$nextTick(() => { this.isSyncingParentUpdate = false })
    },

    handleItemAdded(item, type, index) {
      let newItem = new QuoteItemModel(this.innerValue.quoteId, '', item.itemDesc, ItemCategoryTypes.PART)
      newItem.lineNumber = !this.isAudanet ? this.nextLineNumber : 0
      newItem.itemNo = this.$filters.pad(newItem.lineNumber, 4)
      newItem.itemQuantity = 1
      newItem.value = 0
      newItem.mark = PartTypes.NEW.value
      newItem.markupPercent = 0
      newItem.markupValue = 0
      newItem.buyPrice = 0
      newItem.partNo = ''
      newItem.side = ''
      // replace the item with the new item
      this.innerValue.parts.splice(index, 1, newItem)

      this.$emit('onItemCopied', newItem)
    },
    addBlankItem() {
      let newItem = new QuoteItemModel(this.innerValue.quoteId, '', '', ItemCategoryTypes.PART)
      newItem.lineNumber = !this.isAudanet ? this.nextLineNumber : 0
      newItem.itemNo = this.$filters.pad(newItem.lineNumber, 4)
      newItem.itemQuantity = 1
      newItem.value = 0
      newItem.mark = PartTypes.NEW.value
      newItem.markupPercent = 0
      newItem.markupValue = 0
      newItem.buyPrice = 0
      newItem.partNo = ''
      newItem.side = ''
      newItem.sortNo = this.innerValue.parts.length ? Math.max(...this.innerValue.parts.map((i) => i.sortNo)) + 1 : 1
      if (this.isAudanet) {
        newItem.rev = QuoteItemRevTypes.NonAudaNetPart
      }
      this.innerValue.parts.splice(this.innerValue.parts.length, 1, newItem)
      this.innerValue.lines = this.nextLineNumber + 1
      this.$notification.success('', 'Item added')

      this.$nextTick(() => {
        this.$emit('newItemAdded', newItem.quoteItemId)
      })
    },
    deleteLinkedItem(item) {
      if (!this.isNtar) return
      // Primary: follow the part's own pointer to the paint loading.
      // Fallback: search by the reverse link (paint loading points back to this part).
      // The fallback handles parts whose linkedItemId was not persisted server-side
      // (e.g. parts that were sent to PartsCheck and reloaded from the server).
      let linkedItem = Guid.validGuid(item.linkedItemId)
        ? this.innerValue.labours.find((i) => i.quoteItemId === item.linkedItemId && i.isLoadItem && !i.deleted)
        : null
      if (!linkedItem) {
        linkedItem = this.innerValue.labours.find((i) => i.linkedItemId === item.quoteItemId && i.isLoadItem && !i.deleted)
      }
      if (linkedItem) {
        this.deletePaintLoadingItem(linkedItem)
      }
    },
    deleteItem(item) {
      this.deleteLinkedItem(item)
      if (item.isNew) {
        const itemIndex = this.innerValue.parts
          .map(function (obj) {
            return obj.quoteItemId
          })
          .indexOf(item.quoteItemId)
        if (itemIndex >= 0) {
          this.innerValue.parts.splice(itemIndex, 1)
          // this.items.splice(index, 1)
        }
      } else {
        this.autoSaveDeleteExistingItem(item)
      }
      this.updateTotal()

      // Sync the deletion into the Add-Item panel so it isn't re-added.
      this.$eventHub.$emit(EventHubTypes.QuoteItemDeletedSync, item)

      // Propagate the deletion to the parent directly (mirrors itemMoved) rather than
      // relying on the innerValue watcher, which drops emits while isSyncingParentUpdate
      // is true — otherwise a later value echo resets innerValue and resurrects the item.
      this.lastEmittedPartsSignature = this.computePartsSignature(this.innerValue.parts)
      this.lastEmittedPaintLoadingSig = this.computePartsSignature((this.innerValue.labours || []).filter(l => l.isLoadItem))
      this.isSyncingParentUpdate = true
      this.$emit('handleUpdate', { ...this.value, parts: cloneDeep(this.innerValue.parts), labours: this.computeEmitLabours(this.innerValue.labours) })
      this.$nextTick(() => { this.isSyncingParentUpdate = false })

      this.$emit('onItemDeleted', item.lineNumber)

      // this.$toast.open({
      //   message: `Item <span class="is-italic">${item.itemDesc}</span> deleted`,
      //   type: 'is-danger',
      //   position: 'is-top',
      //   actionText: 'Undo',
      //   onAction: () => {
      //     if (item.isNew) {
      //       this.innerValue.parts.splice(this.innerValue.parts.length, 1, item)
      //     } else {
      //       item.deleted = false
      //       item.isDeleted = false
      //       item.itemStatus = originalItemStatus
      //     }
      //     this.updateTotal()
      //   }
      // })
    },

    async goToPartsCheck(e, partsCheckReferenceNo, searchType, part) {
      const isManualPartOrder = !part.partStatus && (part.quantityOrdered !== 0 || part.quantityReceived !== 0)
      if (isManualPartOrder) {
        this.$nextTick(() => {
          this.$router.push({
            name: QuoteRoutes.QuotePartsControl.name
          })
        })
        return
      }
      // Not priced Yet
      if (!part.partStatus) {
        this.$notification.warning('', 'Please request the part before proceeding to PartsCheck.')

        // this.$toast.open({
        //   message: 'Please request the part before proceeding to PartsCheck.',
        //   type: 'is-warning',
        //   position: 'is-bottom',
        //   queue: false
        // })
        return
      } else if (
        ///// requested and/or priced // pending order
        (part.partStatus === 'Exp' && part.quantityOrdered == 0 && part.quantityReceived == 0) ||
        (part.partStatus === 'Imp' && part.quantityOrdered == 0 && part.quantityReceived == 0) ||
        (part.partStatus === 'Pord' && part.quantityOrdered == 0 && part.quantityReceived == 0)
      ) {
        '/' + this.innerValue.subQuoteNo
        const quoteNo = this.innerValue.prefix + this.innerValue.quoteNo + this.innerValue.suffix
        const result = await PartsCheckService.goToPartsCheck(quoteNo, this.innerValue.subQuoteNo, partsCheckReferenceNo, searchType)
        if (result) window.open(result, '_blank')
      } else if (
        ///// ordered, received, returned, etc.
        (part.quantityOrdered > 0 && part.quantityReceived == 0 && part.quantityReturned == 0) ||
        (part.quantityOrdered > 0 && part.quantityReceived > 0 && part.quantityOrdered == part.quantityReceived) ||
        (part.quantityOrdered > 0 && part.quantityReceived > 0 && part.quantityOrdered !== part.quantityReceived) ||
        (part.quantityOrdered > 0 && part.quantityReturned > 0 && part.quantityOrdered == part.quantityReturned) ||
        (part.quantityOrdered > 0 && part.quantityReturned > 0 && part.quantityOrdered !== part.quantityReturned)
      ) {
        const quoteNo = this.innerValue.prefix + this.innerValue.quoteNo + this.innerValue.suffix
        const result = await PartsCheckService.goToPartsCheck(quoteNo, this.innerValue.subQuoteNo, partsCheckReferenceNo, 'po')
        if (result) window.open(result, '_blank')
      }
    },
    updateTotal() {}
  }
}
</script>

<style scoped>
/* Your component's styles go here */
</style>